import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useAuth, SignedIn, SignedOut } from "@clerk/clerk-react";
import { ApiReferenceReact } from "@scalar/api-reference-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Loader2, Key, AlertCircle } from "lucide-react";
import { useApiKeysStore } from "@/stores/apiKeysStore";
import { revealApiKey } from "@/lib/api";

import "@scalar/api-reference-react/style.css";

// ============================================================================
// Key Management Banner
// ============================================================================

function KeyBanner({
  onKeyResolved,
}: {
  onKeyResolved: (key: string | null) => void;
}) {
  const { getToken } = useAuth();
  const { keys, loading, creating, fetchKeys, createKey } = useApiKeysStore();
  const [newKeyName, setNewKeyName] = useState("");
  const [selectedKeyIdRaw, setSelectedKeyId] = useState<string>("");
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);

  // Auto-select first key when keys load
  const selectedKeyId = selectedKeyIdRaw || (keys.length > 0 ? String(keys[0].id) : "");

  // Reveal key by id
  const revealKey = async (keyId: string) => {
    if (!keyId) {
      onKeyResolved(null);
      return;
    }
    const allKeys = useApiKeysStore.getState().keys;
    const key = allKeys.find((k) => k.id === Number(keyId));
    if (!key?.copyable) {
      setRevealError(
        "This key can't be auto-filled. Copy it from the API Keys page."
      );
      onKeyResolved(null);
      return;
    }
    setRevealing(true);
    setRevealError(null);
    try {
      const result = await revealApiKey(getToken, Number(keyId));
      onKeyResolved(result.key);
    } catch {
      setRevealError(
        "This key can't be auto-filled. Copy it from the API Keys page."
      );
      onKeyResolved(null);
    } finally {
      setRevealing(false);
    }
  };

  const handleSelectKey = (keyId: string) => {
    setSelectedKeyId(keyId);
    void revealKey(keyId);
  };

  // Fetch keys on mount and reveal first key
  useEffect(() => {
    fetchKeys(getToken).then(() => {
      const store = useApiKeysStore.getState();
      if (store.keys.length > 0 && !selectedKeyIdRaw) {
        void revealKey(String(store.keys[0].id));
      }
    });
  }, [fetchKeys, getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    const key = await createKey(getToken, newKeyName || undefined);
    if (key) {
      setNewKeyName("");
      // The store adds the new key — find it and select it
      // The key is returned directly from createKey, so we can resolve immediately
      onKeyResolved(key);
      // Select the newly created key (it's the last in the array after store update)
      // We need to wait a tick for the store to update
      setTimeout(() => {
        const store = useApiKeysStore.getState();
        const newest = store.keys[store.keys.length - 1];
        if (newest) {
          setSelectedKeyId(String(newest.id));
        }
      }, 0);
    }
  };

  if (loading) {
    return (
      <div className="border-b bg-muted/30 px-4 py-3 sm:px-6 lg:px-8">
        <div className="container mx-auto flex items-center gap-3">
          <Key className="h-4 w-4 text-muted-foreground" />
          <Skeleton className="h-5 w-48" />
        </div>
      </div>
    );
  }

  // No keys yet — show inline creation
  if (keys.length === 0) {
    return (
      <div className="border-b bg-muted/30 px-4 py-3 sm:px-6 lg:px-8">
        <div className="container mx-auto flex flex-wrap items-center gap-3">
          <Key className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Create an API key to try endpoints:
          </span>
          <Input
            placeholder="Key name (optional)"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            className="h-8 w-48"
          />
          <Button size="sm" onClick={handleCreate} disabled={creating}>
            {creating ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Plus className="h-3 w-3" />
            )}
            Create API Key
          </Button>
        </div>
      </div>
    );
  }

  // Has keys — show picker
  return (
    <div className="border-b bg-muted/30 px-4 py-3 sm:px-6 lg:px-8">
      <div className="container mx-auto flex flex-wrap items-center gap-3">
        <Key className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">API Key:</span>
        <Select value={selectedKeyId} onValueChange={handleSelectKey}>
          <SelectTrigger className="h-8 w-64">
            <SelectValue placeholder="Select a key" />
          </SelectTrigger>
          <SelectContent>
            {keys.map((key) => (
              <SelectItem key={key.id} value={String(key.id)}>
                {key.name
                  ? `${key.name} (${key.prefix}...${key.suffix})`
                  : `${key.prefix}...${key.suffix}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {revealing && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        {revealError && (
          <span className="flex items-center gap-1 text-sm text-destructive">
            <AlertCircle className="h-3 w-3" />
            {revealError}
          </span>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setSelectedKeyId("");
            onKeyResolved(null);
            handleCreate();
          }}
          disabled={creating}
        >
          {creating ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Plus className="h-3 w-3" />
          )}
          New key
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// DocsPage
// ============================================================================

const DocsPage = () => {
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);

  // Scalar key: changes when resolvedKey changes, forcing remount
  const scalarKey = resolvedKey ?? "no-key";

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] flex-col">
      {/* Key management banner */}
      <SignedOut>
        <div className="border-b bg-muted/30 px-4 py-3 sm:px-6 lg:px-8">
          <div className="container mx-auto flex items-center gap-3">
            <Key className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              <Link
                to="/sign-in?redirect_url=/docs"
                className="text-primary underline"
              >
                Sign in
              </Link>{" "}
              and create an API key to try the endpoints, or paste a key
              manually in the authentication panel below.
            </span>
          </div>
        </div>
      </SignedOut>

      <SignedIn>
        <KeyBanner
          onKeyResolved={(key) => {
            setResolvedKey(key);
          }}
        />
      </SignedIn>

      {/* Scalar API Reference */}
      <div className="flex-1">
        <ApiReferenceReact
          key={scalarKey}
          configuration={{
            url: "/openapi.json",
            theme: "default",
            darkMode: false,
            hideDarkModeToggle: true,
            withDefaultFonts: false,
            authentication: {
              preferredSecurityScheme: "bearerAuth",
              ...(resolvedKey
                ? {
                    securitySchemes: {
                      bearerAuth: { token: resolvedKey },
                    },
                  }
                : {}),
            },
          }}
        />
      </div>
    </div>
  );
};

export default DocsPage;
